import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { RouterLink } from '@angular/router';
import { toRefusal } from '@core/helpers/installation-refusal.helper';
import { MigrationFinding } from '@core/models/migration-report.model';
import { MigrationReportService } from '@core/services/migration-report.service';
import {
  DialogShellComponent,
  FilledButtonComponent,
  TextButtonComponent,
} from '@custom-components';
import { DetailFieldComponent } from '@custom-components/general/detail-field';
import { TextFieldComponent } from '@custom-components/general/form/text-field/text-field.component';
import { TranslateModule } from '@ngx-translate/core';
import { evidenceEntries } from '../../finding-evidence';
import { PlacedFindingRefusal, placeFindingRefusal } from '../../finding-refusal';
import { findingLink } from '../../migration-finding-link';

export interface MigrationFindingDialogData {
  finding: MigrationFinding;
  // Acknowledging is its own feature, separate from reading the report.
  canResolve: boolean;
  partnerName: string | null;
}

export function migrationFindingDialogConfig(
  data: MigrationFindingDialogData,
): MatDialogConfig<MigrationFindingDialogData> {
  return {
    panelClass: 'no-padding-dialog',
    width: '40rem',
    maxWidth: 'calc(100vw - 2rem)',
    autoFocus: 'dialog',
    data,
  };
}

// Closes with the finding as acknowledged, or with nothing when dismissed. `stale` tells the
// opener the finding is gone, so it must read the report again.
@Component({
  selector: 'xemex-migration-finding-dialog',
  imports: [
    DatePipe,
    DetailFieldComponent,
    DialogShellComponent,
    FilledButtonComponent,
    FormsModule,
    RouterLink,
    TextButtonComponent,
    TextFieldComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './migration-finding-dialog.component.html',
  styleUrl: './migration-finding-dialog.component.scss',
})
export class MigrationFindingDialogComponent {
  readonly data = inject<MigrationFindingDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<MigrationFindingDialogComponent, MigrationFinding>>(MatDialogRef);
  private readonly service = inject(MigrationReportService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly finding = this.data.finding;
  protected readonly evidence = evidenceEntries(this.finding.evidence);
  protected readonly link = findingLink(this.finding);

  // Only an open finding can be acknowledged, and only by a caller who holds the feature.
  protected readonly canAcknowledge = this.data.canResolve && this.finding.resolution === 'open';

  readonly note = signal('');
  readonly confirming = signal(false);
  readonly isSaving = signal(false);
  readonly refusal = signal<PlacedFindingRefusal | null>(null);
  readonly stale = computed(() => this.refusal()?.stale ?? false);

  protected readonly noteMissing = computed(() => this.note().trim().length === 0);

  // The note is checked here before anything is sent; the confirmation follows.
  review(): void {
    if (this.noteMissing()) return;
    this.refusal.set(null);
    this.confirming.set(true);
  }

  back(): void {
    this.confirming.set(false);
  }

  acknowledge(): void {
    const note = this.note().trim();
    if (!this.confirming() || note.length === 0 || this.isSaving()) return;
    this.isSaving.set(true);
    this.refusal.set(null);
    this.service
      .acknowledge(this.finding.findingOid, note)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: finding => this.dialogRef.close(finding),
        error: (error: unknown) => {
          this.isSaving.set(false);
          this.confirming.set(false);
          this.refusal.set(placeFindingRefusal(toRefusal(error)));
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }
}
